import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import dayjs from 'dayjs';
import { FileSpreadsheet, FileText, Loader2 } from 'lucide-react';
import { GuardianAttendanceApi, GuardianAttendanceSummaryRow } from '@/services/guardian-attendance.api';
import { SchoolsApi } from '@/services/superadmin.api';
import { OrgPortalApi } from '@/services/org.api';
import type { ExportFormat } from '@/services/school.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Badge } from '@/components/ui/Badge';
import { Input, Select } from '@/components/ui/Input';
import { DataTable, useDataTable } from '@/components/data-table';

const columnHelper = createColumnHelper<GuardianAttendanceSummaryRow>();

/** Matches the old system's "Parent Attendance Summary" screen. */
export function ParentAttendanceSummaryPage({ scope }: { scope: 'superadmin' | 'organization' | 'school' }) {
  const showSchoolPicker = scope !== 'school';
  const [schoolId, setSchoolId] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [busy, setBusy] = useState<ExportFormat | null>(null);
  const dt = useDataTable({ defaultLimit: 20 });

  const { data: schools = [] } = useQuery({
    queryKey: ['schools-for-parent-attendance-summary', scope],
    queryFn: async () =>
      scope === 'organization' ? (await OrgPortalApi.listSchools({ limit: 100 })).items : (await SchoolsApi.list({ limit: 100 })).items,
    enabled: showSchoolPicker,
  });

  // Same "default to a populated period" rule as student attendance — data
  // is dead since 2025, so seed the range from the most recent date that
  // actually has data (that whole month), not "today".
  const { data: latest } = useQuery({
    queryKey: ['guardian-attendance-latest-for-summary', schoolId],
    queryFn: () => GuardianAttendanceApi.list({ limit: 1, schoolId: showSchoolPicker ? schoolId || undefined : undefined }),
  });

  useEffect(() => {
    const latestDate = latest?.items[0]?.date;
    if (latestDate) {
      setEndDate((d) => d || latestDate);
      setStartDate((d) => d || dayjs(latestDate).startOf('month').format('YYYY-MM-DD'));
    }
  }, [latest]);

  useEffect(() => {
    dt.onPaginationChange((prev) => ({ ...prev, pageIndex: 0 }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolId, startDate, endDate]);

  const { data: allRows = [], isFetching } = useQuery({
    queryKey: ['guardian-attendance-summary', schoolId, startDate, endDate],
    queryFn: () =>
      GuardianAttendanceApi.summary({
        schoolId: showSchoolPicker ? schoolId || undefined : undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
      }),
    enabled: !!startDate && !!endDate,
  });

  // Bounded, pre-aggregated rollup — paginate client-side over the
  // already-loaded full array rather than adding server-side pagination,
  // same pattern as OrgSchoolFeesSummaryPage/StudentSummaryReportPage.
  const filtered = dt.searchInput.trim()
    ? allRows.filter((r) => r.guardianName.toLowerCase().includes(dt.searchInput.trim().toLowerCase()))
    : allRows;
  const total = filtered.length;
  const totalPages = Math.max(1, Math.ceil(total / dt.limit));
  const page = Math.min(dt.page, totalPages);
  const pageRows = filtered.slice((page - 1) * dt.limit, page * dt.limit);

  const runExport = async (format: ExportFormat) => {
    if (busy) return;
    setBusy(format);
    try {
      await GuardianAttendanceApi.exportSummary(format, {
        schoolId: showSchoolPicker ? schoolId || undefined : undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
      });
    } finally {
      setBusy(null);
    }
  };

  const columns = [
    columnHelper.display({
      id: 'no',
      header: 'No.',
      enableSorting: false,
      cell: (info) => <span className="text-slate-400">{(page - 1) * dt.limit + info.row.index + 1}</span>,
    }),
    columnHelper.accessor('guardianName', { header: 'Guardian' }),
    ...(showSchoolPicker ? [columnHelper.accessor('schoolName', { header: 'School' })] : []),
    columnHelper.accessor('present', { header: 'Present', cell: (ctx) => <Badge tone="green">{ctx.getValue()}</Badge> }),
    columnHelper.accessor('absent', { header: 'Absent', cell: (ctx) => <Badge tone="red">{ctx.getValue()}</Badge> }),
    columnHelper.accessor('total', { header: 'Total' }),
    columnHelper.display({
      id: 'status',
      header: 'Status',
      cell: (info) => (info.row.original.notMarked ? <Badge tone="slate">Not Marked</Badge> : null),
    }),
  ];

  return (
    <>
      <PageHeader
        title="Parent Attendance Summary"
        description="Present/absent totals per guardian over a date range."
        actions={
          <div className="inline-flex overflow-hidden rounded-md border border-slate-200">
            <button
              type="button"
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50"
              onClick={() => runExport('xlsx')}
              disabled={!!busy}
              title="Export to Excel"
            >
              {busy === 'xlsx' ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 text-green-600" />}
              Excel
            </button>
            <div className="w-px bg-slate-200" />
            <button
              type="button"
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50"
              onClick={() => runExport('pdf')}
              disabled={!!busy}
              title="Export to PDF"
            >
              {busy === 'pdf' ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4 text-red-600" />}
              PDF
            </button>
          </div>
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Input
          value={dt.searchInput}
          onChange={(e) => dt.setSearchInput(e.target.value)}
          placeholder="Search by guardian name"
          className="w-64"
        />
        {showSchoolPicker && (
          <Select className="!w-56" value={schoolId} onChange={(e) => setSchoolId(e.target.value)}>
            <option value="">All schools</option>
            {schools.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </Select>
        )}
        <Input type="date" className="!w-40" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
        <span className="text-sm text-slate-400">to</span>
        <Input type="date" className="!w-40" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
      </div>

      <DataTable
        columns={columns}
        result={{ items: pageRows, total, page, limit: dt.limit, totalPages }}
        state={dt}
        loading={isFetching}
        emptyMessage="No guardian attendance recorded for the selected range."
        getRowId={(row) => `${row.guardianId}-${row.schoolId}`}
      />
    </>
  );
}
